<template>
    <form
        class="w-full form-control"
        :class="{
            'border-t border-bg-base-300 lg:border-none p-4 lg:p-0': !isInteractive
        }"
        @submit.prevent="onSubmit()"
    >
        <div class="flex items-center gap-2 mb-4">
            <MiscIcon name="gift" class="text-base-content" />
            <label for="applyCoupon" class="font-semibold text-lg">{{ $t('cart.coupons.label') }}</label>
        </div>

        <div v-if="coupons" data-cy="couponList">
            <div
                v-for="(coupon, index) in coupons"
                :key="index"
                class="flex justify-between items-center pb-4 text-sm"
            >
                <div class="flex flex-col">
                    <div class="flex">
                        <span>
                            {{ t('cart.coupons.code') }}:
                        </span>
                        <span class="font-semibold pl-4 truncate max-w-[189px] sm:max-w-none whitespace-nowrap overflow-hidden">
                            {{ coupon.payload?.code }}
                        </span>
                    </div>

                    <div class="flex">
                        <span>
                            {{ t('cart.coupons.value') }}:
                        </span>
                        <span v-if="coupon?.price?.totalPrice" class="font-semibold text-new-focus pl-4">
                            {{ formatCouponValue(coupon?.price?.totalPrice) }}
                        </span>
                    </div>
                </div>
                <button
                    type="button"
                    class="btn btn-sm btn-icon-left btn-ghost"
                    :disabled="isCartWriteInFlight"
                    :aria-label="t('cart.coupons.title') + ' ' + t('cart.coupons.remove')"
                    @click="onRemoveLineItem(coupon)"
                >
                    <MiscIcon name="x" />
                    <span class="hidden sm:inline-block">{{ t('cart.coupons.remove') }}</span>
                </button>
            </div>
        </div>
        <div
            class="w-full flex justify-between gap-3"
            :class="{
                'flex-col' : showCol && isInteractive,
                'flex-col lg:flex-row' : showCol && !isInteractive
            }"
        >
            <input
                id="applyCoupon"
                v-model="code"
                :disabled="loading"
                :aria-disabled="loading"
                required
                type="text"
                :placeholder="$t('cart.coupons.placeholder')"
                class="w-full input text-base"
            >
            <button
                :disabled="loading"
                type="submit"
                :aria-disabled="loading"
                class="btn btn-primary btn-icon-left"
            >
                <MiscIcon name="check" class="text-base-100" />
                <span v-if="loading" class="loading" />
                {{ $t('cart.coupons.button') }}
            </button>
        </div>
    </form>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { ApiClientError } from '@shopware/api-client'

const props = defineProps<{
    showCol?: boolean,
    coupons?: Schemas['LineItem'][],
    isInteractive?: boolean,
    bgColor?: string
}>()

const { t } = useI18n()
const { cart, addPromotionCode, removeItem, refreshCart, isCartWriteInFlight } = useCart()
const { showNotification } = useNotification()
const code = ref('')
const loading = ref(false)
const { getFormattedPrice } = usePrice()
const nuxtApp = useNuxtApp()

const cartErrors = computed(() => {
    if (!cart.value?.errors) return []
    return Array.isArray(cart.value.errors)
        ? cart.value.errors
        : Object.values(cart.value.errors)
})

const couponErrorKeyMap: Record<string, string> = {
    'gift-card-not-approved-error': 'cart.errors.voucherNotApproved',
    'gift-card-currency-mismatch-error': 'cart.errors.voucherCurrencyMismatch',
    'gift-card-no-amount-left': 'cart.errors.voucherNoAmountLeft',
}

async function onSubmit () {
    const trimmedCode = code.value.trim()
    if (!trimmedCode) return

    loading.value = true
    try {
        await addPromotionCode(trimmedCode)

        if (cartErrors.value.length > 0) {
            cartErrors.value.forEach(error => {
                const i18nKey = couponErrorKeyMap[error.messageKey]
                showNotification(
                    i18nKey ? t(i18nKey, { code: trimmedCode }) : error.message,
                    error.level > 0 ? 'error' : 'success',
                    true
                )
            })
        }
    } catch (e: unknown) {
        // Shopware aborted the cart calculation, so the rendered cart no longer matches the
        // server state -- resync before reporting anything.
        await refreshCart()

        const errorCode = e instanceof ApiClientError ? e.details?.errors?.[0]?.code : undefined

        showNotification(
            // CHECKOUT__CART_LINE_ITEM_NOT_STACKABLE means the calculation tried to merge a line
            // item that already exists and is not stackable (LineItemCollection::add ->
            // LineItem::setQuantity). Promotion discount line items are non-stackable by
            // default, so this is reached when the same promotion ends up in the cart twice.
            // The raw Shopware message names an internal line item id, so it is not shown.
            errorCode === 'CHECKOUT__CART_LINE_ITEM_NOT_STACKABLE'
                ? t('cart.errors.voucherNotCombinable')
                : (e instanceof ApiClientError ? e.details?.errors?.[0]?.detail : undefined)
                    ?? (e instanceof Error ? e.message : String(e)),
            'error',
            true
        )
    } finally {
        loading.value = false
        code.value = ''
        if (!props.isInteractive) {
            nuxtApp.callHook('applyVoucher')
        }
    }
}

async function onRemoveLineItem (lineItem: Schemas['LineItem']) {
    await removeItem(lineItem)

    if (!cartErrors.value.length) {
        showNotification(t('cart.list.removeSuccess'), 'success')
        nuxtApp.callHook('removeFromCart', { product: lineItem })
        if (!props.isInteractive) {
            nuxtApp.callHook('applyVoucher')
        }
    }
}

function formatCouponValue (value: number) {
    let newValue = getFormattedPrice(value)
    if (newValue.includes('-')) {
        newValue = newValue.slice(1)
    }
    return newValue
}
</script>
